// Phase 5C — Duplicate detection helpers for Parts Assist intake.
// Soft, advisory only — never blocks, never auto-merges.
//
// Two match modes:
//   1. Exact part number (case-insensitive, whitespace-trimmed)
//   2. Fuzzy description (token overlap >= 0.7 after normalization)

export interface DuplicateCandidate {
  id: string;
  description: string;
  vendor_part_number: string | null;
  job_id: string | null;
}

export interface DuplicateMatch<T extends DuplicateCandidate> {
  match: T;
  /** 'part_number' = exact PN, 'description' = fuzzy desc match */
  reason: 'part_number' | 'description';
}

const STOP = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'for', 'with',
  'to', 'in', 'on', 'by', 'kit', 'set', 'pack',
]);

function normalize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !STOP.has(t));
}

function tokenOverlap(a: string, b: string): number {
  const ta = new Set(normalize(a));
  const tb = new Set(normalize(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  let hit = 0;
  for (const t of ta) if (tb.has(t)) hit++;
  // Symmetric overlap (Dice-like): 2 * |A ∩ B| / (|A| + |B|)
  return (2 * hit) / (ta.size + tb.size);
}

/**
 * Find the strongest duplicate candidate, scoped to the same ticket
 * AND filtered by job (when a job is selected, only same-job rows count;
 * when whole-ticket is selected, only ticket-level rows count).
 *
 * Returns null when nothing matches.
 */
export function findDuplicate<T extends DuplicateCandidate>(
  existing: T[],
  input: { description: string; part_number: string | null; job_id: string | null },
  opts: { fuzzyThreshold?: number } = {},
): DuplicateMatch<T> | null {
  const threshold = opts.fuzzyThreshold ?? 0.7;
  const targetJob = input.job_id ?? null;
  const scoped = existing.filter((l) => (l.job_id ?? null) === targetJob);

  // 1. Exact part number wins.
  const pn = (input.part_number ?? '').trim().toLowerCase();
  if (pn) {
    const exact = scoped.find(
      (l) => (l.vendor_part_number ?? '').trim().toLowerCase() === pn,
    );
    if (exact) return { match: exact, reason: 'part_number' };
  }

  // 2. Fuzzy description fallback.
  const desc = input.description.trim();
  if (desc.length >= 4) {
    let best: DuplicateMatch<T> | null = null;
    let bestScore = 0;
    for (const row of scoped) {
      const score = tokenOverlap(desc, row.description ?? '');
      if (score >= threshold && score > bestScore) {
        bestScore = score;
        best = { match: row, reason: 'description' };
      }
    }
    if (best) return best;
  }

  return null;
}
